// Saving progress, so the user can put the phone down in the shop and pick it
// up where they left it.
//
// localStorage, deliberately. The data is small, it belongs to the person
// holding the device, and there is no account and no server to sync it to in
// this release — inventing one to hold a shopping list would be a bigger claim
// than the feature makes.
//
// Three things this has to survive, because all three happen to real people:
//
//   - Storage being unavailable at all. Private browsing, blocked site data, a
//     locked-down enterprise profile: reading `localStorage` can *throw*, not
//     just return null. Every access is wrapped, and the app runs unpersisted
//     rather than not running.
//   - The quota being full. A failed write is not something the user can act
//     on mid-recipe, so it fails quietly and the session continues in memory.
//   - A record written by an older build. Versioned, and a mismatch is thrown
//     away rather than merged. A half-understood record restored into a screen
//     is a bug that presents itself to the user as data loss.
//
// Nothing here is React-aware; `src/hooks/usePersistentState.js` is the part
// that knows about components.

// Renamed from `pickuppilot` with the app. Records under the old prefix are not
// migrated and are not read: they simply stop being found, which reads to the
// app as a first run and re-seeds. That is acceptable only because this has not
// shipped — after release, a prefix change is a data loss and needs a migration
// rather than a rename.
const PREFIX = 'notdelivery';

// Bump when a stored shape changes incompatibly. Old records are discarded on
// read, so a bump costs the user their in-progress meal and nothing more.
export const STATE_VERSION = 1;

export function storageKey(key) {
  return `${PREFIX}:v${STATE_VERSION}:${key}`;
}

// Even *reaching* for localStorage can throw, so the getter is guarded too.
function storageOrNull(override) {
  if (override !== undefined) return override;
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export function loadState(key, { storage } = {}) {
  const target = storageOrNull(storage);
  if (!target) return null;

  let raw;
  try {
    raw = target.getItem(storageKey(key));
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Corrupt beyond reading. Drop it rather than leave it to fail on every
    // future load.
    clearState(key, { storage });
    return null;
  }

  if (!parsed || typeof parsed !== 'object') return null;
  if (parsed.version !== STATE_VERSION) {
    clearState(key, { storage });
    return null;
  }
  return 'data' in parsed ? parsed.data : null;
}

// Returns whether it stuck, for callers that care. Most don't: a meal that
// failed to save is still a meal you can cook.
export function saveState(key, data, { storage } = {}) {
  const target = storageOrNull(storage);
  if (!target) return false;
  try {
    target.setItem(storageKey(key), JSON.stringify({ version: STATE_VERSION, data }));
    return true;
  } catch {
    return false;
  }
}

export function clearState(key, { storage } = {}) {
  const target = storageOrNull(storage);
  if (!target) return false;
  try {
    target.removeItem(storageKey(key));
    return true;
  } catch {
    return false;
  }
}

// First run and "the user cleared it" are the same thing to us, and both mean
// "use the seed". The distinction only matters where an empty list is a real
// choice the user made — History's `clearAll` is the case — so that state is
// stored as an empty array, which is not the same as an absent record.
export function loadOrSeed(key, seed, { storage } = {}) {
  const stored = loadState(key, { storage });
  return stored === null ? seed : stored;
}
